import {
  GetSetupIntentResponse,
  GetOrderEstimateResponse,
  ListActiveOrdersForPatient,
  GuestValidationInput,
  SendPaymentChoiceInput,
} from "@app/preBill/models";
import PreBillService from "@app/service/preBill";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { GraphQLFormattedError } from "graphql";
import { useQuery } from "@app/utils";
import {
  PreBillErrorResponse,
  SendSurveyAnswerInput,
  SurveyAnswerResponse,
} from "../models/apiControllerModel";

export type UsePreBillApiControllerType = {
  getSetupIntent: () => Promise<GetSetupIntentResponse>;
  getOrderEstimate: () => Promise<GetOrderEstimateResponse>;
  listActiveOrdersForPatient: () => Promise<ListActiveOrdersForPatient>;
  guestValidation: (input: GuestValidationInput) => Promise<void>;
  sendPaymentChoice: (input: SendPaymentChoiceInput) => Promise<void>;
  sendSurveyAnswer: (
    input: SendSurveyAnswerInput
  ) => Promise<SurveyAnswerResponse>;
};

const extractPreBillError = (
  gqlError: GraphQLFormattedError | undefined
): PreBillErrorResponse | undefined => {
  const preBillError = gqlError?.extensions?.preBillError;
  if (
    preBillError &&
    typeof preBillError === "object" &&
    "code" in preBillError
  ) {
    return preBillError as PreBillErrorResponse;
  }
  return undefined;
};

const throwPreBillError = (
  errors: ReadonlyArray<GraphQLFormattedError> | undefined,
  apolloError?: ApolloError,
  fallbackMessage = "Pre-bill request failed"
): never => {
  const firstGqlError = errors?.[0] ?? apolloError?.graphQLErrors?.[0];
  const preBillError = extractPreBillError(firstGqlError);
  if (preBillError) {
    throw preBillError;
  }
  if (apolloError) {
    throw apolloError;
  }
  if (firstGqlError) {
    throw new Error(firstGqlError.message);
  }
  throw new Error(fallbackMessage);
};

const usePreBillApiController = (): UsePreBillApiControllerType => {
  const query = useQuery();
  const token = query.get("token") ?? "";

  const [guestValidationMutation] = useMutation(
    PreBillService.guestValidation(),
    { errorPolicy: "all" }
  );
  const [sendPaymentChoiceMutation] = useMutation(
    PreBillService.sendPaymentChoice(),
    { errorPolicy: "all" }
  );
  const [sendSurveyAnswerMutation] = useMutation(
    PreBillService.sendSurveyAnswer(),
    { errorPolicy: "all" }
  );

  const [getOrderEstimateQuery] = useLazyQuery(
    PreBillService.getOrderEstimate(),
    {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    }
  );
  const [getSetupIntentQuery] = useLazyQuery(PreBillService.getSetupIntent(), {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });
  const [listActiveOrdersQuery] = useLazyQuery(
    PreBillService.listActiveOrdersForPatient(),
    { fetchPolicy: "no-cache", errorPolicy: "all" }
  );

  const guestValidation = async (
    input: GuestValidationInput
  ): Promise<void> => {
    const { errors } = await guestValidationMutation({
      variables: { token, input },
    });
    if (errors?.length) {
      throwPreBillError(errors, undefined, "guestValidation failed");
    }
  };

  const getOrderEstimate = async (): Promise<GetOrderEstimateResponse> => {
    const { data, error, errors } = await getOrderEstimateQuery({
      variables: { token },
    });
    if (error || errors?.length || !data?.getOrderEstimate) {
      throwPreBillError(errors, error, "getOrderEstimate failed");
    }
    return data.getOrderEstimate as GetOrderEstimateResponse;
  };

  const getSetupIntent = async (): Promise<GetSetupIntentResponse> => {
    const { data, error, errors } = await getSetupIntentQuery({
      variables: { token },
    });
    if (error || errors?.length || !data?.getSetupIntent) {
      throwPreBillError(errors, error, "getSetupIntent failed");
    }
    return data.getSetupIntent as GetSetupIntentResponse;
  };

  const sendPaymentChoice = async (
    input: SendPaymentChoiceInput
  ): Promise<void> => {
    const { errors } = await sendPaymentChoiceMutation({
      variables: { token, input },
    });
    if (errors?.length) {
      throwPreBillError(errors, undefined, "sendPaymentChoice failed");
    }
  };

  const sendSurveyAnswer = async (
    input: SendSurveyAnswerInput
  ): Promise<SurveyAnswerResponse> => {
    const { data, errors } = await sendSurveyAnswerMutation({
      variables: { token, input },
    });
    if (errors?.length || !data?.sendSurveyAnswer) {
      throwPreBillError(errors, undefined, "sendSurveyAnswer failed");
    }
    return data.sendSurveyAnswer as SurveyAnswerResponse;
  };

  const listActiveOrdersForPatient =
    async (): Promise<ListActiveOrdersForPatient> => {
      const { data, error, errors } = await listActiveOrdersQuery();
      if (error || errors?.length || !data?.listActiveOrdersForPatient) {
        throwPreBillError(errors, error, "listActiveOrdersForPatient failed");
      }
      return data.listActiveOrdersForPatient as ListActiveOrdersForPatient;
    };

  return {
    getSetupIntent,
    getOrderEstimate,
    listActiveOrdersForPatient,
    guestValidation,
    sendPaymentChoice,
    sendSurveyAnswer,
  };
};

export default usePreBillApiController;
